import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { colorTokens } from '../app/designTokens';
import { PublicOnboardingShell } from '../components/PublicOnboardingShell';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { resetPassword } from '../api/authApi';

const { semantic } = colorTokens;

function getResetErrorMessage(err, t) {
  const data = err?.response?.data;
  if (data?.errors?.password?.[0]) {
    return data.errors.password[0];
  }
  if (data?.message) {
    return data.message;
  }
  return t('auth:reset_password_failed');
}

export function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const token = (tokenParam || '').trim();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!token) {
      setError(t('auth:reset_password_failed'));
      return;
    }

    if (password !== passwordConfirmation) {
      setError(t('auth:password_mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({
        email: email.trim(),
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      navigate('/login', {
        replace: true,
        state: { message: t('auth:reset_password_success') },
      });
    } catch (err) {
      setError(getResetErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicOnboardingShell
      maxWidth="sm"
      title={t('auth:reset_password_title')}
      subtitle={t('auth:reset_password_subtitle')}
      footer={(
        <Typography component="span" sx={{ fontSize: 12, color: semantic.text.muted }}>
          <Box
            component={RouterLink}
            to="/login"
            sx={{ color: semantic.text.secondary, textDecoration: 'underline' }}
          >
            {t('auth:back_to_login')}
          </Box>
        </Typography>
      )}
      testId="public-reset-password-shell"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="reset-password-error">
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextInput
            label={t('auth:email')}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputProps={{ 'data-testid': 'reset-password-email' }}
          />
          <TextInput
            label={t('auth:password')}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            inputProps={{ 'data-testid': 'reset-password-password' }}
          />
          <TextInput
            label={t('auth:confirm_password')}
            type="password"
            required
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            autoComplete="new-password"
            inputProps={{ 'data-testid': 'reset-password-confirmation' }}
          />
          <Button
            type="submit"
            variant="Primary"
            fullWidth
            disabled={submitting || !email.trim() || !password || !passwordConfirmation}
            loading={submitting}
          >
            {t('auth:reset_password_submit')}
          </Button>
        </Stack>
      </Box>
    </PublicOnboardingShell>
  );
}
